import React, { ForwardedRef, forwardRef } from 'react';
import classNames from 'classnames';
import _get from 'lodash/get';
import CSSTransition from 'react-transition-group/CSSTransition';
import Typography from '@lib/components/Typography/Typography';
import { useWatch } from 'react-hook-form';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import CheckBoxAsToggle from '@lib/components/CheckBoxAsToggle/CheckBoxAsToggle';
import FormItem from '@lib/components/ReactHookForm/FormItem';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import { Values } from '@lib/interfaces/form';
import styles from './FrequencyFormFields.module.scss';

const duration = 150;

const defaultStyle = {
  transition: `${duration}ms ease-in-out`,
  opacity: 0,
};

const transitionStyles = {
  entering: { opacity: 1 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

export interface FrequencyFormFieldsProps {
  children: React.ReactNode;
  description?: string;
  disabled?: boolean;
  isLocked?: boolean;
  isVisibleFn?: (values: Values) => boolean;
  lockTooltipText?: string;
  name: string;
  title: React.ReactNode;
  titleTooltipText?: string;
}

const FrequencyFormFields = forwardRef(
  (props: FrequencyFormFieldsProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      children,
      title,
      description,
      disabled,
      isLocked,
      lockTooltipText,
      name,
      isVisibleFn,
      titleTooltipText,
    } = props;
    const watch = useWatch();
    const nodeRef = React.useRef<HTMLDivElement>(null);
    const enableCheckboxFieldName = `${name}_enabled`;
    const isChecked = !!_get(watch, enableCheckboxFieldName);
    const isVisible = isVisibleFn ? isVisibleFn(watch) : true;
    if (!isVisible) return null;
    return (
      <div
        className={classNames(styles.fieldRoot, {
          [styles.fieldRootEnabled]: isChecked,
        })}
        ref={ref}
      >
        <div>
          <FormItem
            name={enableCheckboxFieldName}
            element={CheckBoxSingleBool}
            componentProps={{
              options: [{ label: 'checked', value: 'checked' }],
              CheckBoxComponent: CheckBoxAsToggle,
              disabled,
            }}
            formItemClassName={styles.fieldToggleFormItem}
          />
        </div>
        <div>
          <div className={styles.fieldContent}>
            <div className={styles.fieldTitle}>
              <Typography variant="body" strong>
                {title}
              </Typography>
              {titleTooltipText && (
                <Tooltip body={titleTooltipText}>
                  <MaterialIcon
                    cursor="help"
                    icon="info_outline"
                    size="md-16"
                    className={styles.fieldTitleIcon}
                  />
                </Tooltip>
              )}
              {isLocked && (
                <span className={styles.fieldTitleLock}>
                  <Tooltip body={lockTooltipText}>
                    <Chip
                      type={ChipTypes.chipBlue}
                      size={ChipSizes.small}
                      leftIcon="lock"
                      asBlockElement
                    />
                  </Tooltip>
                </span>
              )}
            </div>
            {description && (
              <Typography variant="caption" className={styles.fieldDescription}>
                {description}
              </Typography>
            )}
          </div>
          <CSSTransition
            in={isChecked}
            timeout={duration}
            unmountOnExit
            nodeRef={nodeRef}
          >
            {(transitionState) => (
              <div
                style={{
                  ...defaultStyle,
                  // @ts-ignore
                  ...transitionStyles[transitionState],
                }}
                className={styles.fieldChildren}
              >
                {children}
              </div>
            )}
          </CSSTransition>
        </div>
      </div>
    );
  },
);

export default FrequencyFormFields;
